import { useState, type ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { DataTable, SortableTh, Th } from '@/ui/data-table'
import { VirtualTableRows } from '@/ui/virtual-table-rows'

import type { AdHocQueryLogicalType, QueryCell } from '../lib/query-result'

export type GridResult = {
  columns: { name: string; type: AdHocQueryLogicalType }[]
  rows: QueryCell[][]
}

export type GridSort = { field: string; direction: 'asc' | 'desc'; onSort: (field: string) => void }

const ROW_ESTIMATE_PX = 33

export function formatQueryCell(value: QueryCell): string {
  if (value === null) return 'null'
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return String(value)
}

export function QueryResultsGrid({
  result,
  label = 'Query results',
  labels,
  hasMore = false,
  footer,
  formatCell,
  sort,
}: {
  result: GridResult
  label?: string
  labels?: Record<string, string>
  hasMore?: boolean
  footer?: (scrollElement: HTMLElement | null) => ReactNode
  formatCell?: (column: string, value: QueryCell) => string | undefined
  sort?: GridSort
}) {
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null)
  const rows = result.rows.map((cells, index) => ({ index, cells }))
  const headers = [
    <Th key="#" className="w-12 text-right" aria-label="Row">#</Th>,
    ...result.columns.map((column, columnIndex) => sort ? (
      <SortableTh
        key={`${column.name}:${columnIndex}`}
        className="min-w-56 whitespace-nowrap"
        label={labels?.[column.name] ?? column.name}
        direction={sort.field === column.name ? sort.direction : undefined}
        onSort={() => sort.onSort(column.name)}
      />
    ) : (
      <Th key={`${column.name}:${columnIndex}`} className="min-w-56 whitespace-nowrap">{labels?.[column.name] ?? column.name}</Th>
    )),
  ]

  if (rows.length === 0) {
    return <p role="status" data-run-state="empty" className="text-muted-foreground p-5 text-xs">The query returned no rows.</p>
  }

  return (
    <div ref={setScrollElement} data-testid="query-results-scroll" className="min-h-0 flex-1 overflow-auto">
      <DataTable
        caption={label}
        headers={headers}
        empty={false}
        emptyMessage={null}
        rowCount={hasMore ? -1 : rows.length + 1}
      >
        <VirtualTableRows
          items={rows}
          getKey={(row) => String(row.index)}
          estimateSize={ROW_ESTIMATE_PX}
          scrollElement={scrollElement}
          columnCount={headers.length}
          renderRow={(row, rowProps) => (
            <tr {...rowProps} className="border-border hover:bg-muted/35 border-b font-mono text-[11px]">
              <td className="text-muted-foreground px-3 py-1.5 text-right tabular-nums">{row.index + 1}</td>
              {row.cells.map((cell, cellIndex) => {
                const text = formatCell?.(result.columns[cellIndex]?.name ?? '', cell) ?? formatQueryCell(cell)
                return (
                  <td
                    key={cellIndex}
                    title={text}
                    className={cn('max-w-md truncate px-3 py-1.5', cell === null && 'text-muted-foreground italic')}
                  >
                    {text}
                  </td>
                )
              })}
            </tr>
          )}
        />
      </DataTable>
      {footer?.(scrollElement)}
    </div>
  )
}
